import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatPriceWithCurrency } from '../../utils/vat';

// Date-only, matching OrganizationDetailDialog. The shared utils formatDate
// appends HH:mm, which reads as "00:00" for the plain DateFields used here.
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fi-FI') : '-');
const formatDateTime = (value) => (value ? new Date(value).toLocaleString('fi-FI') : '-');

// Every user-facing string comes from a backend CODE, never a backend sentence:
// Django resolves _() from the browser Accept-Language header, which is routinely
// English even when the app UI is Finnish.
const REASON_KEYS = {
  no_subscription: 'vadmin.cancelSubReasonNoSubscription',
  stripe_subscription: 'vadmin.cancelSubReasonStripe',
  stripe_linked: 'vadmin.cancelSubReasonStripeLinked',
  not_active: 'vadmin.cancelSubReasonNotActive',
  no_last_access_date: 'vadmin.cancelSubReasonNoDate',
  invalid_date: 'vadmin.cancelSubReasonInvalidDate',
  no_scheduled_cancellation: 'vadmin.cancelSubReasonNothingScheduled',
  already_effective: 'vadmin.cancelSubReasonAlreadyEffective',
};

const PAID_THROUGH_KEYS = {
  yearly_plan_fee: 'vadmin.cancelSubPaidThroughYearly',
  monthly_plan_fee: 'vadmin.cancelSubPaidThroughMonthly',
  yearly_no_plan_fee_date: 'vadmin.cancelSubPaidThroughYearlyFallback',
  unknown: 'vadmin.cancelSubPaidThroughUnknown',
};

const WARNING_KEYS = {
  past_date: 'vadmin.cancelSubWarningPastDate',
  after_paid_through: 'vadmin.cancelSubWarningAfterPaidThrough',
  before_paid_through: 'vadmin.cancelSubWarningBeforePaidThrough',
};

/**
 * Staff panel for ending an invoice-billed subscription on a chosen date.
 *
 * The client keeps access through the chosen last access day; afterwards the
 * SubscriptionGate blocks them and no further subscription invoices are generated.
 * Stripe subscriptions are not cancellable here — the client does that themselves
 * in the Stripe billing portal.
 */
export default function CancelSubscriptionPanel({ organizationId, onChanged }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [info, setInfo] = useState(null);

  const [lastAccessDate, setLastAccessDate] = useState('');
  const [reason, setReason] = useState('');
  const [billFinalPeriod, setBillFinalPeriod] = useState(true);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Translate a backend error_code, falling back to a generic message rather than
  // ever showing the backend's own (possibly English) sentence.
  const translateError = useCallback(
    (data) => {
      const key = REASON_KEYS[data?.error_code];
      return key ? t(key) : t('vadmin.cancelSubFailed');
    },
    [t]
  );

  const translateWarning = useCallback(
    (warning) => {
      const key = WARNING_KEYS[warning?.code];
      if (!key) return null;
      const params = warning.params || {};
      return t(key, {
        paidThrough: formatDate(params.paid_through),
        chosen: formatDate(params.chosen),
      });
    },
    [t]
  );

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await axios.get(
        `/api/vadmin/organizations/${organizationId}/cancellation-info/`
      );
      setInfo(res.data);
      setLastAccessDate(res.data.suggested_last_access_date || '');
      setBillFinalPeriod(res.data.cancellation_bill_final_period !== false);
      setReason(res.data.cancellation_reason || '');
    } catch (err) {
      setLoadError(err.response?.data?.detail || t('vadmin.cancelSubLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const submit = async (confirmed) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(
        `/api/vadmin/organizations/${organizationId}/cancel-subscription/`,
        {
          last_access_date: lastAccessDate,
          reason,
          bill_final_period: billFinalPeriod,
          confirmed,
        }
      );
      setConfirmOpen(false);
      setWarnings([]);
      setSuccessMessage(t('vadmin.cancelSubScheduled'));
      await fetchInfo();
      if (onChanged) await onChanged();
    } catch (err) {
      const data = err.response?.data;
      if (data?.requires_confirmation) {
        // Backend wants the staff user to see the money consequences first.
        setWarnings(data.warnings || []);
        setConfirmOpen(true);
      } else {
        setConfirmOpen(false);
        setSubmitError(translateError(data));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUndo = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(
        `/api/vadmin/organizations/${organizationId}/undo-subscription-cancellation/`,
        {}
      );
      setSuccessMessage(t('vadmin.cancelSubUndone'));
      await fetchInfo();
      if (onChanged) await onChanged();
    } catch (err) {
      setSubmitError(translateError(err.response?.data));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  // Stripe / no subscription / not active — nothing to do here, just explain why.
  if (!info?.eligible && !info?.has_pending_cancellation) {
    const reasonKey = REASON_KEYS[info?.reason_code];
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {reasonKey ? t(reasonKey) : t('vadmin.cancelSubNotAvailable')}
      </Alert>
    );
  }

  const pendingDrafts = info.pending_draft_invoices || [];

  // Already scheduled — show what will happen and offer to undo.
  if (info.has_pending_cancellation) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          {t('vadmin.cancelSubTitle')}
        </Typography>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {t('vadmin.cancelSubScheduledFor', {
              date: formatDate(info.last_access_date),
            })}
          </AlertTitle>
          <Typography variant="body2">
            {t('vadmin.cancelSubAccessEndsExplanation', {
              date: formatDate(info.end),
            })}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {info.cancellation_bill_final_period
              ? t('vadmin.cancelSubFinalPeriodWillBeInvoiced')
              : t('vadmin.cancelSubFinalPeriodWillNotBeInvoiced')}
          </Typography>
          {(info.cancellation_scheduled_by || info.cancellation_scheduled_at) && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              {t('vadmin.cancelSubScheduledBy', {
                who: info.cancellation_scheduled_by || '-',
                when: formatDateTime(info.cancellation_scheduled_at),
              })}
            </Typography>
          )}
          {info.cancellation_reason && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('vadmin.cancelSubReasonLabel')}: {info.cancellation_reason}
            </Typography>
          )}
        </Alert>

        <Button variant="outlined" onClick={handleUndo} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : t('vadmin.cancelSubUndo')}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('vadmin.cancelSubTitle')}
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {t('vadmin.cancelSubIntro')}
      </Typography>

      {info.paid_through && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('vadmin.cancelSubPaidThrough', { date: formatDate(info.paid_through) })}{' '}
          {t(PAID_THROUGH_KEYS[info.paid_through_source] || PAID_THROUGH_KEYS.unknown)}
        </Alert>
      )}

      {pendingDrafts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {t('vadmin.cancelSubPendingDrafts', { count: pendingDrafts.length })}
          </AlertTitle>
          <Typography variant="body2">{t('vadmin.cancelSubPendingDraftsHelp')}</Typography>
          <List dense disablePadding>
            {pendingDrafts.map((inv) => (
              <ListItem key={inv.id} disableGutters>
                <ListItemText
                  primary={
                    <Link href={`/invoices/${inv.id}`} target="_blank" rel="noopener noreferrer">
                      {inv.invoice_number || `#${inv.id}`}
                    </Link>
                  }
                  secondary={formatDate(inv.created_at)}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
        <TextField
          type="date"
          size="small"
          label={t('vadmin.cancelSubLastAccessDate')}
          value={lastAccessDate}
          onChange={(e) => setLastAccessDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText={t('vadmin.cancelSubLastAccessDateHelp')}
          sx={{ width: { xs: '100%', sm: 240 } }}
        />
        <TextField
          size="small"
          label={t('vadmin.cancelSubReasonLabel')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText={t('vadmin.cancelSubReasonHelp')}
          sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}
        />
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={billFinalPeriod}
            onChange={(e) => setBillFinalPeriod(e.target.checked)}
          />
        }
        label={t('vadmin.cancelSubBillFinalPeriod')}
      />
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: parseFloat(info.unbilled_metered_gross) > 0 ? 0 : 2,
          color: 'text.secondary',
        }}
      >
        {t('vadmin.cancelSubBillFinalPeriodHelp')}
      </Typography>
      {/* Small metered usage accumulates unbilled until it reaches the minimum invoice
          total, so unticking the box above can discard several months' worth. Show what
          is actually at stake. */}
      {parseFloat(info.unbilled_metered_gross) > 0 && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 2, color: billFinalPeriod ? 'text.secondary' : 'warning.main' }}
        >
          {t('vadmin.cancelSubUnbilledMetered', {
            // `count` (not `months`) so i18next picks the _one / _other variant.
            count: info.unbilled_metered_months,
            amount: formatPriceWithCurrency(info.unbilled_metered_gross),
            since: formatDate(info.unbilled_metered_since),
          })}
        </Typography>
      )}

      <Button
        variant="contained"
        color="error"
        onClick={() => submit(false)}
        disabled={!lastAccessDate || submitting}
      >
        {submitting && !confirmOpen ? <CircularProgress size={20} /> : t('vadmin.cancelSubButton')}
      </Button>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('vadmin.cancelSubConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('vadmin.cancelSubConfirmIntro', { date: formatDate(lastAccessDate) })}
          </Typography>
          {warnings.map((warning, index) => {
            const text = translateWarning(warning);
            if (!text) return null;
            return (
              <Alert key={warning.code || index} severity="warning" sx={{ mb: 1 }}>
                {text}
              </Alert>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => submit(true)}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : t('vadmin.cancelSubConfirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
